import { z } from "zod";

/* --------------------------------------------------------------- locations */

/** Where a fridge item lives. The UI offers these three. */
export const STORAGE_LOCATIONS = ["fridge", "freezer", "pantry"] as const;
export type StorageLocation = (typeof STORAGE_LOCATIONS)[number];

export const STORAGE_LOCATION_LABELS: Record<StorageLocation, string> = {
  fridge: "Fridge",
  freezer: "Freezer",
  pantry: "Pantry",
};

/* ------------------------------------------------------------------- names */

/**
 * The key an ingredient and a fridge item are matched on.
 *
 * Deliberately blunt: lowercase, strip accents and punctuation, collapse
 * whitespace, and singularise a simple plural, so "Eggs", "egg" and "EGG"
 * all land on `egg`. Anything cleverer belongs in a later pass — a wrong
 * match here silently deducts the wrong item.
 */
export function normalizeIngredientName(raw: string): string {
  const cleaned = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";

  const words = cleaned.split(" ");
  words[words.length - 1] = singularize(words[words.length - 1]!);
  return words.join(" ");
}

function singularize(word: string): string {
  if (word.length <= 3) return word;
  if (word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (/(?:ch|sh|s|x|z|o)es$/.test(word)) return word.slice(0, -2);
  if (word.endsWith("s") && !word.endsWith("ss") && !word.endsWith("us")) return word.slice(0, -1);
  return word;
}

/* ------------------------------------------------------------------- units */

type Dimension = "mass" | "volume" | "count";

type UnitDef = {
  /** Quantities only convert within one dimension. */
  dimension: Dimension;
  /** How many base units (g, ml, or pieces) one of this unit is. */
  base: number;
  label: string;
};

/**
 * The units a quantity can be written in.
 *
 * `""` is a bare count — "3 eggs" — and is the default, so a user who never
 * touches the unit picker still gets real comparisons.
 */
export const UNITS = {
  "": { dimension: "count", base: 1, label: "" },
  pcs: { dimension: "count", base: 1, label: "pcs" },
  g: { dimension: "mass", base: 1, label: "g" },
  kg: { dimension: "mass", base: 1000, label: "kg" },
  oz: { dimension: "mass", base: 28.349523125, label: "oz" },
  lb: { dimension: "mass", base: 453.59237, label: "lb" },
  ml: { dimension: "volume", base: 1, label: "ml" },
  l: { dimension: "volume", base: 1000, label: "l" },
  tsp: { dimension: "volume", base: 4.92892159375, label: "tsp" },
  tbsp: { dimension: "volume", base: 14.78676478125, label: "tbsp" },
  cup: { dimension: "volume", base: 236.5882365, label: "cup" },
} as const satisfies Record<string, UnitDef>;

export type Unit = keyof typeof UNITS;

export const UNIT_KEYS = Object.keys(UNITS) as Unit[];

/** Spellings that mean one of the canonical units above. */
const UNIT_ALIASES: Record<string, Unit> = {
  piece: "pcs", pieces: "pcs", pc: "pcs", pcs: "pcs", x: "pcs", unit: "pcs", units: "pcs",
  gram: "g", grams: "g", gr: "g", gm: "g",
  kilogram: "kg", kilograms: "kg", kilo: "kg", kilos: "kg",
  ounce: "oz", ounces: "oz",
  pound: "lb", pounds: "lb", lbs: "lb",
  milliliter: "ml", milliliters: "ml", millilitre: "ml", millilitres: "ml",
  liter: "l", liters: "l", litre: "l", litres: "l", lt: "l",
  teaspoon: "tsp", teaspoons: "tsp", tsps: "tsp",
  tablespoon: "tbsp", tablespoons: "tbsp", tbsps: "tbsp", tbs: "tbsp",
  cups: "cup",
};

/** Canonical unit key, or null when we do not recognise it. */
export function normalizeUnit(raw: string | null | undefined): Unit | null {
  const key = (raw ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (key === "") return "";
  if (key in UNITS) return key as Unit;
  return UNIT_ALIASES[key] ?? null;
}

/**
 * Converts between units of the same dimension.
 *
 * Returns null rather than a guess when either unit is unknown or the two sit
 * in different dimensions — "200 g of milk" against "1 l of milk" is not a
 * conversion we can make without a density we do not have.
 */
export function convertQuantity(
  quantity: number,
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  const a = normalizeUnit(from);
  const b = normalizeUnit(to);
  if (a === null || b === null) return null;
  const defA = UNITS[a];
  const defB = UNITS[b];
  if (defA.dimension !== defB.dimension) return null;
  return (quantity * defA.base) / defB.base;
}

/* ------------------------------------------------------------------- stock */

export type Amount = { quantity: number | null; unit: string };

/**
 * - `have`    — enough of it in the fridge
 * - `partial` — some, but not enough
 * - `missing` — none at all
 * - `unknown` — it is in the fridge, but the amounts cannot be compared
 *               (either side has no quantity, or the units do not convert)
 */
export type StockStatus = "have" | "partial" | "missing" | "unknown";

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  have: "In fridge",
  partial: "Not enough",
  missing: "Missing",
  unknown: "In fridge",
};

/** `have` is null when nothing in the fridge matched the ingredient. */
export function compareStock(need: Amount, have: Amount | null): StockStatus {
  if (!have) return "missing";
  if (need.quantity === null || have.quantity === null) return "unknown";

  const available = convertQuantity(have.quantity, have.unit, need.unit);
  if (available === null) return "unknown";
  if (available >= need.quantity) return "have";
  if (available > 0) return "partial";
  return "missing";
}

/**
 * What the fridge row should hold after cooking with it, in the fridge's own
 * unit. Null when the amounts do not compare — the caller leaves the row alone
 * rather than deducting a number it cannot justify.
 */
export function deductStock(need: Amount, have: Amount): number | null {
  if (need.quantity === null || have.quantity === null) return null;
  const used = convertQuantity(need.quantity, need.unit, have.unit);
  if (used === null) return null;
  return Math.max(0, roundQuantity(have.quantity - used));
}

/** Quantities are numeric(10,3) in the database; keep arithmetic in step. */
export function roundQuantity(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** "1.5 kg", "3", "" — how a quantity reads next to its name. */
export function formatAmount(amount: Amount): string {
  if (amount.quantity === null) return "";
  const qty = String(roundQuantity(amount.quantity));
  const unit = normalizeUnit(amount.unit);
  const label = unit === null ? amount.unit.trim() : UNITS[unit].label;
  return label ? `${qty} ${label}` : qty;
}

/* ----------------------------------------------------------------- schemas */

const nameSchema = z.string().trim().min(1).max(120);
const quantitySchema = z.number().nonnegative().max(1_000_000).nullish();
const unitSchema = z.string().trim().max(20).optional();

export const createPantryItemSchema = z.object({
  name: nameSchema,
  quantity: quantitySchema,
  unit: unitSchema,
  location: z.enum(STORAGE_LOCATIONS).optional(),
  expiresAt: z.string().date().nullish(),
  teamId: z.string().uuid().nullish(),
});

export const updatePantryItemSchema = z.object({
  name: nameSchema.optional(),
  quantity: quantitySchema,
  unit: unitSchema,
  location: z.enum(STORAGE_LOCATIONS).optional(),
  expiresAt: z.string().date().nullish(),
});

export const listPantryQuerySchema = z.object({
  teamId: z.string().uuid().optional(),
});

export const createIngredientSchema = z.object({
  name: nameSchema,
  quantity: quantitySchema,
  unit: unitSchema,
  note: z.string().trim().max(200).nullish(),
});

export const updateIngredientSchema = createIngredientSchema.partial();

export const cookRecipeSchema = z.object({
  /** Cook anyway when something is missing, deducting only what is there. */
  force: z.boolean().optional(),
});

export type CreatePantryItemInput = z.infer<typeof createPantryItemSchema>;
export type UpdatePantryItemInput = z.infer<typeof updatePantryItemSchema>;
export type CreateIngredientInput = z.infer<typeof createIngredientSchema>;
export type UpdateIngredientInput = z.infer<typeof updateIngredientSchema>;
